/**
 * LAWITY - Authentication Store
 * Real Firebase Authentication implementation
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updatePassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
  User as FirebaseUser,
  multiFactor,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  MultiFactorError,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'attorney' | 'staff' | 'client';
  firmId?: string;
  twoFactorEnabled: boolean;
  lastLogin?: Date;
}

interface Session {
  id: string;
  device: string;
  browser: string;
  location: string;
  ip: string;
  createdAt: Date;
  lastActive: Date;
  isCurrent: boolean;
}

interface AuthState {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  initialized: boolean;
  error: string | null;
  sessions: Session[];
  mfaResolver: any | null;
  
  // Auth methods
  signIn: (email: string, password: string) => Promise<{ requires2FA: boolean }>;
  verify2FA: (code: string) => Promise<boolean>;
  verifyRecoveryCode: (code: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  
  // 2FA methods
  enable2FA: (secret: string, code: string) => Promise<boolean>;
  disable2FA: (code: string) => Promise<boolean>;
  generateRecoveryCodes: () => Promise<string[]>;
  
  // Session methods
  getSessions: () => Promise<Session[]>;
  revokeSession: (sessionId: string) => Promise<void>;
  revokeAllSessions: () => Promise<void>;
  
  // Password methods
  changePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<boolean>;
  
  // Internal
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  initializeAuth: () => void;
}

// Session detection
function detectDevice(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android Device';
  if (/Mac/.test(ua)) return 'MacBook';
  if (/Windows/.test(ua)) return 'Windows PC';
  return 'Unknown Device';
}

function detectBrowser(): string {
  const ua = navigator.userAgent;
  if (/Chrome/.test(ua)) return 'Chrome';
  if (/Firefox/.test(ua)) return 'Firefox';
  if (/Safari/.test(ua)) return 'Safari';
  if (/Edge/.test(ua)) return 'Edge';
  return 'Unknown Browser';
}

// Convert Firebase user to our User type
async function firebaseUserToUser(firebaseUser: FirebaseUser): Promise<User> {
  // Get additional user data from Firestore
  const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
  const userData = userDoc.data();
  
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    name: userData?.name || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
    role: userData?.role || 'staff',
    firmId: userData?.firmId,
    twoFactorEnabled: multiFactor(firebaseUser).enrolledFactors.length > 0,
    lastLogin: userData?.lastLogin?.toDate(),
  };
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      firebaseUser: null,
      isAuthenticated: false,
      isLoading: true,
      initialized: false,
      error: null,
      sessions: [],
      mfaResolver: null,

      initializeAuth: () => {
        onAuthStateChanged(auth, async (firebaseUser) => {
          if (firebaseUser) {
            try {
              const user = await firebaseUserToUser(firebaseUser);
              
              // Update last login
              await updateDoc(doc(db, 'users', firebaseUser.uid), {
                lastLogin: serverTimestamp(),
              }).catch(() => {
                // User doc may not exist yet
              });
              
              set({
                user,
                firebaseUser,
                isAuthenticated: true,
                isLoading: false,
                initialized: true,
              });
            } catch (error) {
              console.error('Error loading user data:', error);
              set({
                user: null,
                firebaseUser: null,
                isAuthenticated: false,
                isLoading: false,
                initialized: true,
              });
            }
          } else {
            set({
              user: null,
              firebaseUser: null,
              isAuthenticated: false,
              isLoading: false,
              initialized: true,
            });
          }
        });
      },

      signIn: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const user = await firebaseUserToUser(userCredential.user);
          
          set({
            user,
            firebaseUser: userCredential.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          
          return { requires2FA: false };
        } catch (error: any) {
          // Check if this is a multi-factor auth error
          if (error.code === 'auth/multi-factor-auth-required') {
            const resolver = getMultiFactorResolver(auth, error as MultiFactorError);
            set({ 
              mfaResolver: resolver,
              isLoading: false,
              error: null,
            });
            return { requires2FA: true };
          }
          
          // Handle other errors
          let errorMessage = 'An error occurred during sign in';
          switch (error.code) {
            case 'auth/user-not-found':
            case 'auth/wrong-password':
            case 'auth/invalid-credential':
              errorMessage = 'Invalid email or password';
              break;
            case 'auth/too-many-requests':
              errorMessage = 'Too many failed attempts. Please try again later.';
              break;
            case 'auth/user-disabled':
              errorMessage = 'This account has been disabled';
              break;
          }
          
          set({ isLoading: false, error: errorMessage });
          throw new Error(errorMessage);
        }
      },

      verify2FA: async (code: string) => {
        const { mfaResolver } = get();
        
        if (!mfaResolver) {
          set({ error: 'No pending 2FA verification' });
          return false;
        }
        
        set({ isLoading: true, error: null });
        
        try {
          // Get the TOTP hint
          const totpHint = mfaResolver.hints.find(
            (hint: any) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
          );
          
          if (!totpHint) {
            set({ isLoading: false, error: 'TOTP not configured' });
            return false;
          }
          
          const totpAssertion = TotpMultiFactorGenerator.assertionForSignIn(
            totpHint.uid,
            code
          );
          
          const userCredential = await mfaResolver.resolveSignIn(totpAssertion);
          const user = await firebaseUserToUser(userCredential.user);
          
          set({
            user,
            firebaseUser: userCredential.user,
            isAuthenticated: true,
            isLoading: false,
            mfaResolver: null,
            error: null,
          });
          
          return true;
        } catch (error: any) {
          console.error('2FA verification error:', error);
          set({ 
            isLoading: false, 
            error: error.code === 'auth/invalid-verification-code' 
              ? 'Invalid verification code' 
              : 'Verification failed'
          });
          return false;
        }
      },

      verifyRecoveryCode: async (code: string) => {
        // Recovery codes would need custom implementation
        // For now, this is a placeholder
        set({ error: 'Recovery codes not yet implemented' });
        return false;
      },

      signOut: async () => {
        try {
          await firebaseSignOut(auth);
          set({
            user: null,
            firebaseUser: null,
            isAuthenticated: false,
            error: null,
            sessions: [],
            mfaResolver: null,
          });
        } catch (error) {
          console.error('Sign out error:', error);
        }
      },

      enable2FA: async (secret: string, code: string) => {
        const { firebaseUser } = get();
        
        if (!firebaseUser) {
          set({ error: 'Not authenticated' });
          return false;
        }
        
        set({ isLoading: true });
        
        try {
          // This requires additional setup - Firebase TOTP enrollment
          // For now, we'll store 2FA preference in Firestore
          await updateDoc(doc(db, 'users', firebaseUser.uid), {
            twoFactorEnabled: true,
            twoFactorSecret: secret, // In production, encrypt this
          });
          
          const { user } = get();
          if (user) {
            set({
              user: { ...user, twoFactorEnabled: true },
              isLoading: false,
              error: null,
            });
          }
          
          return true;
        } catch (error) {
          console.error('Enable 2FA error:', error);
          set({ isLoading: false, error: 'Failed to enable 2FA' });
          return false;
        }
      },

      disable2FA: async (code: string) => {
        const { firebaseUser } = get();
        
        if (!firebaseUser) {
          set({ error: 'Not authenticated' });
          return false;
        }
        
        set({ isLoading: true });
        
        try {
          await updateDoc(doc(db, 'users', firebaseUser.uid), {
            twoFactorEnabled: false,
            twoFactorSecret: null,
          });
          
          const { user } = get();
          if (user) {
            set({
              user: { ...user, twoFactorEnabled: false },
              isLoading: false,
              error: null,
            });
          }
          
          return true;
        } catch (error) {
          console.error('Disable 2FA error:', error);
          set({ isLoading: false, error: 'Failed to disable 2FA' });
          return false;
        }
      },

      generateRecoveryCodes: async () => {
        const codes: string[] = [];
        for (let i = 0; i < 10; i++) {
          const code = Math.random().toString(36).substring(2, 6).toUpperCase() + '-' +
                       Math.random().toString(36).substring(2, 6).toUpperCase();
          codes.push(code);
        }
        
        // Store hashed codes in Firestore
        const { firebaseUser } = get();
        if (firebaseUser) {
          await updateDoc(doc(db, 'users', firebaseUser.uid), {
            recoveryCodes: codes.map(c => btoa(c)), // Simple encoding, use proper hashing in production
          }).catch(() => {});
        }
        
        return codes;
      },

      getSessions: async () => {
        // Get sessions from Firestore
        const { firebaseUser } = get();
        
        if (!firebaseUser) return [];
        
        // For now, create a session entry for current browser
        const sessions: Session[] = [
          {
            id: '1',
            device: detectDevice(),
            browser: detectBrowser(),
            location: 'Current Location',
            ip: 'Current IP',
            createdAt: new Date(Date.now() - 86400000),
            lastActive: new Date(),
            isCurrent: true,
          },
        ];
        
        set({ sessions });
        return sessions;
      },

      revokeSession: async (sessionId: string) => {
        set({ isLoading: true });
        
        // In production, revoke the session token
        set(state => ({
          sessions: state.sessions.filter(s => s.id !== sessionId),
          isLoading: false,
        }));
      },

      revokeAllSessions: async () => {
        set({ isLoading: true });
        
        // In production, revoke all session tokens except current
        set(state => ({
          sessions: state.sessions.filter(s => s.isCurrent),
          isLoading: false,
        }));
      },

      changePassword: async (currentPassword: string, newPassword: string) => {
        const { firebaseUser } = get();
        
        if (!firebaseUser || !firebaseUser.email) {
          set({ error: 'Not authenticated' });
          return false;
        }
        
        set({ isLoading: true, error: null });
        
        try {
          // Re-authenticate then update password
          await signInWithEmailAndPassword(auth, firebaseUser.email, currentPassword);
          await updatePassword(firebaseUser, newPassword);
          
          set({ isLoading: false, error: null });
          return true;
        } catch (error: any) {
          let errorMessage = 'Failed to change password';
          if (error.code === 'auth/wrong-password') {
            errorMessage = 'Current password is incorrect';
          } else if (error.code === 'auth/weak-password') {
            errorMessage = 'New password is too weak';
          }
          
          set({ isLoading: false, error: errorMessage });
          return false;
        }
      },

      requestPasswordReset: async (email: string) => {
        try {
          await sendPasswordResetEmail(auth, email);
        } catch (error) {
          console.error('Password reset error:', error);
          // Don't reveal if email exists
        }
      },

      resetPassword: async (token: string, newPassword: string) => {
        set({ isLoading: true });
        
        try {
          await confirmPasswordReset(auth, token, newPassword);
          set({ isLoading: false, error: null });
          return true;
        } catch (error: any) {
          let errorMessage = 'Failed to reset password';
          if (error.code === 'auth/expired-action-code') {
            errorMessage = 'Reset link has expired';
          } else if (error.code === 'auth/invalid-action-code') {
            errorMessage = 'Invalid reset link';
          }
          
          set({ isLoading: false, error: errorMessage });
          return false;
        }
      },

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // Don't persist sensitive data - let Firebase handle auth state
      }),
    }
  )
);

// Hook for checking if 2FA is pending
export function usePending2FA() {
  const mfaResolver = useAuth(state => state.mfaResolver);
  return {
    isPending: !!mfaResolver,
    clear: () => useAuth.setState({ mfaResolver: null }),
  };
}

// Initialize auth listener on app start
if (typeof window !== 'undefined') {
  useAuth.getState().initializeAuth();
}
